/**
 * Pure geometry helpers for the door/window phase-1 gizmo system.
 *
 * `computeOpeningFrame` derives the local (u, v, normal) frame of an
 * OpeningAnnotation directly from its 4-corner polygon. It does NOT read
 * `viewer.viewerMode.associatedPlane` because that's only installed during
 * phase-2 wall focus — in phase-1 (where these gizmos live) the plane is
 * stale. The opening's own geometry is canonical.
 *
 * `computeNeighborDirections` walks the wall skeleton to find the two
 * neighboring wall edges (left and right of the opening's parent wall edge)
 * and returns their unit directions plus the average. Returns null per side
 * when the wall is a dead-end; the parent gizmo controller hides the
 * corresponding "align" button.
 */

import * as THREE from 'three';
import { OpeningAnnotation } from '../../../../utils/PointoramaAnnotation';
import { PointoramaRoom } from '../../../../utils/PointoramaRoom';
import { PointoramaOutline } from '../../../../utils/PointoramaOutline';

/**
 * Local frame of an opening, derived from its 4-corner rectangular polygon.
 *
 * - `center`: arithmetic mean of the 4 corners.
 * - `u`: along-wall direction (horizontal in world), unit length.
 * - `v`: in-plane vertical (≈ world +Z for typical vertical walls), unit length.
 * - `normal`: wall-perpendicular direction (horizontal in world), unit length.
 * - `halfWidth`: half the along-wall span (|p1 - p0| / 2).
 * - `halfHeight`: half the vertical span (|p3 - p0| / 2).
 *
 * The frame is right-handed: `cross(u, v) === normal`.
 */
export interface OpeningFrame {
  center: THREE.Vector3;
  u: THREE.Vector3;
  v: THREE.Vector3;
  normal: THREE.Vector3;
  halfWidth: number;
  halfHeight: number;
}

/**
 * Build an `OpeningFrame` from the annotation's polygon. Returns null when
 * the polygon is degenerate (fewer than 4 points, or two adjacent corners
 * coincide so u/v can't be derived).
 *
 * Convention from `DoorWindowDetectionService.createMeasurementFromOpening`
 * (and `commitNew` in `DoorWindowEditKindAdapter`):
 *   p0 = left-bottom, p1 = right-bottom, p2 = right-top, p3 = left-top.
 * `u` therefore points "right along the wall", `v` points "up" (vertical).
 */
export function computeOpeningFrame(annotation: OpeningAnnotation): OpeningFrame | null {
  const pts = annotation.points;
  if (pts.length < 4) return null;
  const p0 = pts[0].position;
  const p1 = pts[1].position;
  const p3 = pts[3].position;

  // The polygon is a 4-corner rectangle, but the corner ORDER varies by
  // creation path:
  //   - DoorWindowDetectionService writes (BL, BR, TR, TL) — adjacent
  //     edges are p0→p1 (horizontal) and p0→p3 (vertical).
  //   - LineTool's commitNew path passes whatever order the ShapeBuilder
  //     produced, which has no guaranteed correspondence to BL/BR/TR/TL.
  // For an arbitrary winding we still need: the in-plane horizontal axis
  // (`u`), the vertical-ish axis (`v`), and the wall-perpendicular normal.
  // Strategy: take any two adjacent edges (e.g. p0→p1 and p0→p3). One is
  // "more horizontal" (smaller |z|), the other is "more vertical" (larger
  // |z|). Classify by the absolute Z-component magnitude and assign u/v
  // accordingly. The remaining sign ambiguity for u doesn't matter — the
  // rectangle is symmetric in ±u, and `rebuildPolygonWithU` reconstructs
  // it from center.
  const edgeA = new THREE.Vector3().subVectors(p1, p0);
  const edgeB = new THREE.Vector3().subVectors(p3, p0);
  if (edgeA.lengthSq() < 1e-12 || edgeB.lengthSq() < 1e-12) return null;
  const aIsHorizontal = Math.abs(edgeA.z) < Math.abs(edgeB.z);
  const uVec = aIsHorizontal ? edgeA : edgeB;
  const vVec = aIsHorizontal ? edgeB : edgeA;

  const halfWidth = uVec.length() / 2;
  const halfHeight = vVec.length() / 2;
  if (halfWidth < 1e-6 || halfHeight < 1e-6) return null;

  const u = uVec.clone().normalize();
  const v = vVec.clone().normalize();
  // Ensure `v` points along +Z (upward) so rotations around +Z don't
  // surprise the caller with an upside-down rectangle. For a horizontal-
  // ish wall this is a no-op; flipping `v` keeps the polygon vertical-
  // oriented as expected.
  if (v.z < 0) v.negate();
  const normal = new THREE.Vector3().crossVectors(u, v).normalize();
  if (normal.lengthSq() < 1e-9) return null;
  // The normal must be horizontal for a vertical wall (cross of a
  // horizontal u and vertical v is horizontal). Defensive: project to
  // floor plane in case of small drift.
  if (Math.abs(normal.z) > 0.01) {
    normal.z = 0;
    if (normal.lengthSq() < 1e-9) return null;
    normal.normalize();
  }

  const center = new THREE.Vector3();
  for (const p of pts) center.add(p.position);
  center.divideScalar(pts.length);

  return { center, u, v, normal, halfWidth, halfHeight };
}

/**
 * Unit directions of the up-to-two neighbor walls connected to the
 * collinear wall chain the opening sits on, in the floor (XY) plane.
 *
 * Side assignment is by NORMAL half-space (which side of the parent
 * wall the neighbor departs into), not by chain terminus:
 *   - `posNormalDir`: neighbor whose far endpoint sits on the +normal
 *     side of the parent wall.
 *   - `negNormalDir`: neighbor on the −normal side.
 *
 * A single corner has neighbors on both ends of the chain but those
 * neighbors may sit on the SAME normal side (e.g. an L-shape). In that
 * case one slot stays null and the other holds whichever neighbor is
 * geometrically there. The user's UI places one align-parallel button
 * next to each translate arrow (along ±normal), so the side assignment
 * matches the visible button slots directly.
 *
 * `avgDir` is the unit-normalized sum of posNormalDir + negNormalDir
 * when BOTH exist, oriented to align with the parent chain direction
 * so it represents the wall's intended through-line. Used by the
 * "align to average" button that sits along the wall axis (under the
 * rotation ring) when both side slots are filled.
 *
 * For "align parallel" actions: callers rotate the opening so its `u`
 * axis is parallel to posNormalDir/negNormalDir (parallelism is
 * undirected — `applyRotationToAxis` picks the sign closer to current
 * `u`).
 */
/** A neighbor wall edge: its unit direction (XY) and full world segment. */
export interface NeighborEdge {
  dir: THREE.Vector3;
  start: THREE.Vector3;
  end: THREE.Vector3;
}

export interface NeighborDirections {
  /** Neighbor wall on the +normal side of the parent. */
  posNormalDir: THREE.Vector3 | null;
  /** Neighbor wall on the −normal side of the parent. */
  negNormalDir: THREE.Vector3 | null;
  /** Mean direction of the two neighbors, when both exist. */
  avgDir: THREE.Vector3 | null;
  /** Hit edge segments for the highlight (the walls the buttons align to). */
  posNormalEdge: NeighborEdge | null;
  negNormalEdge: NeighborEdge | null;
}

/**
 * Compute neighbor wall directions by raycasting the opening's normal axis
 * against EVERY wall edge on the opening's floor — all room polygon edges plus
 * all outline (perimeter + courtyard) edges. The button next to each translate
 * arrow aligns the opening parallel to the wall ON THAT SIDE: the nearest edge
 * the ±normal ray hits past the opening's own wall.
 *
 * This is deliberately NOT limited to the opening's linked rooms
 * (`opening_room_ids`). Casting only against linked rooms gave inconsistent
 * results — the outward ray would catch another edge of the same room (a
 * spurious second button) or miss a genuine neighbor on an unlinked room.
 * Casting against all floor walls makes each side independent and predictable:
 * two buttons when there's a wall on both sides, one when only one side has a
 * neighbor.
 *
 * Recomputed on every `OpeningGizmoController.refresh` (walls can be edited at
 * any time; the buttons follow).
 *
 * For OUTLINE walls the opening sits on the building perimeter, so only the
 * INWARD side has a meaningful neighbor — the outward ray would wrap across the
 * footprint and hit the far perimeter edge from outside. When outlines exist on
 * the floor, outward casts (those whose first step leaves the footprint) are
 * rejected so the button only appears on the inside.
 *
 * Returns all-null when no ray finds an edge within `MAX_RAY_DISTANCE_M`.
 */
const MAX_RAY_DISTANCE_M = 50;

export function computeNeighborDirections(rooms: PointoramaRoom[], outlines: PointoramaOutline[], frame: OpeningFrame): NeighborDirections {
  const empty: NeighborDirections = { posNormalDir: null, negNormalDir: null, avgDir: null, posNormalEdge: null, negNormalEdge: null };

  // Every wall edge on the floor — all room polygon edges plus all outline
  // (perimeter + courtyard) edges. Each side's ray casts against ALL of them,
  // not just the opening's linked rooms.
  const edges: { a: THREE.Vector3; b: THREE.Vector3 }[] = [];
  for (const room of rooms) {
    const poly = room.getPolygon();
    if (poly.length < 3) continue;
    for (let i = 0; i < poly.length; i++) edges.push({ a: poly[i], b: poly[(i + 1) % poly.length] });
  }
  for (const outline of outlines) {
    for (const e of outline.getAllEdges()) edges.push({ a: e.start, b: e.end });
  }
  if (edges.length === 0) return empty;

  const normal2D = new THREE.Vector2(frame.normal.x, frame.normal.y);
  if (normal2D.lengthSq() < 1e-12) return empty;
  normal2D.normalize();

  // Cast a ray from the opening center along `dir`; return the nearest wall edge
  // it hits past the opening's own wall (`SELF_SKIP_M` skips the parent edge the
  // opening sits on). Both ±normal sides are cast independently — we don't gate
  // by interior/exterior; for orientation it's better to surface a neighbor on
  // each side that has one than to risk suppressing a valid alignment.
  const SELF_SKIP_M = 0.05;
  const raycastFirstEdge = (dirX: number, dirY: number): NeighborEdge | null => {
    let bestT = MAX_RAY_DISTANCE_M;
    let best: NeighborEdge | null = null;
    for (const { a, b } of edges) {
      const sx = b.x - a.x;
      const sy = b.y - a.y;
      const denom = dirX * sy - dirY * sx;
      if (Math.abs(denom) < 1e-9) continue; // parallel
      const ex = a.x - frame.center.x;
      const ey = a.y - frame.center.y;
      const t = (ex * sy - ey * sx) / denom;
      if (t < SELF_SKIP_M) continue;
      const u = (ex * dirY - ey * dirX) / denom;
      if (u < 0 || u > 1) continue;
      if (t < bestT) {
        const len = Math.hypot(sx, sy);
        if (len < 1e-9) continue;
        bestT = t;
        // Edge direction in XY (unit). Sign is undirected for "align parallel";
        // a consistent sense lets the avgDir math pick the right alignment.
        best = { dir: new THREE.Vector3(sx / len, sy / len, 0), start: a.clone(), end: b.clone() };
      }
    }
    return best;
  };

  const posEdge = raycastFirstEdge(+normal2D.x, +normal2D.y);
  const negEdge = raycastFirstEdge(-normal2D.x, -normal2D.y);
  const posNormalDir = posEdge?.dir ?? null;
  const negNormalDir = negEdge?.dir ?? null;

  // Average direction: when both side walls exist, the wall's intended
  // through-line is the mean of the two. Use the parent wall's `u`
  // direction as the orientation reference so we average the
  // UNDIRECTED axes (otherwise opposing neighbors would cancel out).
  let avgDir: THREE.Vector3 | null = null;
  if (posNormalDir && negNormalDir) {
    const u3D = new THREE.Vector3(frame.u.x, frame.u.y, 0);
    const posAligned = posNormalDir.dot(u3D) >= 0 ? posNormalDir.clone() : posNormalDir.clone().negate();
    const negAligned = negNormalDir.dot(u3D) >= 0 ? negNormalDir.clone() : negNormalDir.clone().negate();
    const sum = posAligned.add(negAligned);
    if (sum.lengthSq() > 1e-9) avgDir = sum.normalize();
  }

  return { posNormalDir, negNormalDir, avgDir, posNormalEdge: posEdge, negNormalEdge: negEdge };
}

/**
 * Recompute the opening's 4 corners after replacing its frame's `u` axis
 * with `newU`. Preserves: `v` (vertical), `normal` (recomputed as
 * `cross(newU, v)` projected to keep the rectangle planar), `center`,
 * `halfWidth`, `halfHeight`.
 *
 * Used by the "align parallel" / "align average" actions and by the
 * rotation gizmo: each produces a new `u` direction; the polygon is
 * regenerated around the unchanged center.
 *
 * `newU` is expected to be horizontal-ish (in-plane with the floor); the
 * caller is responsible for that. The function normalises and projects
 * onto the horizontal plane defensively.
 */
export function rebuildPolygonWithU(frame: OpeningFrame, newU: THREE.Vector3): THREE.Vector3[] {
  // Force horizontal: drop any Z drift so the rotated opening stays
  // vertical (v = +Z stays valid). For walls that aren't exactly vertical
  // we'd want a more general projection onto the wall plane, but every
  // door/window in the current data set is on a vertical wall.
  const horizontalU = new THREE.Vector3(newU.x, newU.y, 0);
  if (horizontalU.lengthSq() < 1e-9) return polygonFromFrameAxes(frame.center, frame.u, frame.v, frame.halfWidth, frame.halfHeight);
  horizontalU.normalize();

  // Keep v as the existing v (vertical for normal walls). Recompute the
  // rectangle from (center, horizontalU, frame.v).
  return polygonFromFrameAxes(frame.center, horizontalU, frame.v, frame.halfWidth, frame.halfHeight);
}

/**
 * Generate the 4 polygon corners (p0..p3) for a rectangle of given half-
 * extents centred at `center`, with axes `u` (along-wall) and `v`
 * (vertical). Mirrors the `p0..p3` convention used by
 * `DoorWindowDetectionService` and `commitNew`.
 */
export function polygonFromFrameAxes(center: THREE.Vector3, u: THREE.Vector3, v: THREE.Vector3, halfWidth: number, halfHeight: number): THREE.Vector3[] {
  const uHalf = u.clone().multiplyScalar(halfWidth);
  const vHalf = v.clone().multiplyScalar(halfHeight);
  return [
    center.clone().sub(uHalf).sub(vHalf), // p0: left-bottom
    center.clone().add(uHalf).sub(vHalf), // p1: right-bottom
    center.clone().add(uHalf).add(vHalf), // p2: right-top
    center.clone().sub(uHalf).add(vHalf), // p3: left-top
  ];
}

/**
 * Translate every corner of an opening's polygon by `delta`. Used by the
 * translate handles: `delta = normal * dragDistance`.
 */
export function translatePolygon(annotation: OpeningAnnotation, delta: THREE.Vector3): THREE.Vector3[] {
  return annotation.points.map((p) => p.position.clone().add(delta));
}

